import { createContext } from "react";
import type { ReactNode } from "react";

/**
 * The source-agnostic playback events emitted by `Video.Root`, mapped to their
 * payload shape. These are identical across every video source, so they live on
 * Root. Errors are NOT here: they are source-specific and belong to each player
 * (see `Video.Player`'s own `onError`).
 */
export type VideoEventMap = {
	play: { currentTime: number };
	pause: { currentTime: number };
	ended: { currentTime: number };
	progress: { percent: number; currentTime: number; duration: number };
	buffer: { buffering: boolean };
};

/**
 * Emits a video event over the bridge as `custom:videoRoot:<rootId>:<event>`.
 * Generic over {@link VideoEventMap} so each event only accepts its own payload.
 */
export type VideoEmit = <E extends keyof VideoEventMap>(
	event: E,
	value: VideoEventMap[E],
) => void;

/** Which source-agnostic playback events the app is observing (booleans on the wire). */
export type VideoEventFlags = {
	onPlay?: boolean;
	onPause?: boolean;
	onEnded?: boolean;
	onProgress?: boolean;
	onBuffer?: boolean;
};

/**
 * Shared state provided by `Video.Root` and consumed by `Video.Player`:
 * the bound emitter (keyed on the Root's `__internalId`), the source-agnostic
 * playback modifiers, the event flags, and the overlay children to render on
 * top of the video when the lightbox is open.
 */
export type VideoContextValue = {
	rootInternalId: string;
	emit: VideoEmit;
	autoplay?: boolean;
	muted?: boolean;
	loop?: boolean;
	flags: VideoEventFlags;
	overlay: ReactNode;
};

export const VideoContext = createContext<VideoContextValue | null>(null);
